/*
 *     Copyright (C) 2021 TART K.K.
 *
 *     This program is free software: you can redistribute it and/or modify
 *     it under the terms of the GNU General Public License as published by
 *     the Free Software Foundation, either version 3 of the License, or
 *     (at your option) any later version.
 *
 *     This program is distributed in the hope that it will be useful,
 *     but WITHOUT ANY WARRANTY; without even the implied warranty of
 *     MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *     GNU General Public License for more details.
 *
 *     You should have received a copy of the GNU General Public License
 *     along with this program.  If not, see https://www.gnu.org/licenses/.
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  CircularProgress,
  Button,
} from "@mui/material";

import { ChainId } from "@usedapp/core";
import EtherscanLink from "../../atoms/EtherscanLink";
import MarketplaceLink from "../../atoms/MarketplaceLink";
import styled from "@emotion/styled";
import themes from "../../../themes";

export interface WaitingProcessDialogProps {
  readonly transactionStatus: any;
  readonly isPurchasing: boolean;
  readonly chainId: ChainId | undefined;
  readonly account: string | null | undefined;
  readonly getTokenId?: string | number;
}

const WaitingProcessDialog: React.FC<WaitingProcessDialogProps> = ({
  transactionStatus,
  isPurchasing,
  chainId,
  account,
  getTokenId,
}) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);

  useEffect(() => {
    switch (transactionStatus.status) {
      case "Mining":
        setMessages([]);
        break;
      case "Success":
        setMessages([]);
        break;
      case "Fail":
      case "Exception":
        setMessages([
          "(Possibly wallet balance is insufficient)",
          "Please reload an app and try again.",
        ]);
        break;
      default:
        if (isPurchasing) {
          setMessages(["Starting transaction..."]);
        } else {
          setMessages([]);
        }
    }
  }, [isPurchasing, transactionStatus]);

  useEffect(() => {
    if (isPurchasing || transactionStatus.status !== "None") {
      setOpen(true);
    }
  }, [isPurchasing, transactionStatus.status]);

  return (
    <Dialog
      aria-labelledby="simple-dialog-title"
      open={open}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        square: true,
      }}
    >
      <Container>
        <StyledDialogTitle>
          {transactionStatus.status === "Success"
            ? "You've successfully purchased!"
            : transactionStatus.status === "Mining"
            ? "Please Wait..."
            : `Failed to Purchase`}
        </StyledDialogTitle>
        <Box m={2} textAlign="center">
          {messages.map((message: string, index: number) => (
            <Typography key={index}>{message}</Typography>
          ))}
          {((isPurchasing && transactionStatus.status === "None") ||
            transactionStatus.status === "Mining") && (
            <>
              <Typography>Please wait at the same screen.</Typography>
              <Box m={2}>
                <CircularProgress color={"primary"} size={100} />
              </Box>
              <Box m={2}>
                <EtherscanLink
                  txHash={transactionStatus?.transaction?.hash ?? ""}
                />
              </Box>
            </>
          )}
          {transactionStatus.status === "Success" && (
            <>
              <div style={{ maxWidth: 280, margin: "0 auto" }}>
                <NftWrapper>
                  <iframe
                    src={`https://arweave.net/3mAmpxaFL1iS5AVQ9uvwZPFaDSIooP00PuQA3m7B4QY/?s=${getTokenId}`}
                  />
                </NftWrapper>
              </div>
              <ButtonWrapper>
                <EtherscanLink
                  txHash={transactionStatus?.transaction?.hash ?? ""}
                />
                <MarketplaceLink
                  chainId={chainId?.toString() ?? "1"}
                  market={"opensea"}
                  address={process.env.REACT_APP_NFT_ADDRESS ?? ""}
                  tokenId={getTokenId?.toString() ?? ""}
                />
              </ButtonWrapper>
              <Box>
                <Button
                  variant="contained"
                  color={"primary"}
                  onClick={() => window.location.reload()}
                  fullWidth
                >
                  OK
                </Button>
              </Box>
            </>
          )}
          {(transactionStatus.status === "Fail" ||
            transactionStatus.status === "Exception") && (
            <div style={{ marginTop: 8 }}>
              <Button
                variant="contained"
                color={"primary"}
                onClick={() => window.location.reload()}
                fullWidth
              >
                Reload page
              </Button>
            </div>
          )}
        </Box>
      </Container>
    </Dialog>
  );
};

export default WaitingProcessDialog;

const Container = styled.div`
  background-color: #000;
  color: #fff;
  padding: ${themes.spacing(2)};
  border: 1px solid #fff;
`;

const StyledDialogTitle = styled(DialogTitle)`
  padding: 0;
  text-align: center;
  text-transform: uppercase;
  font-family: "Press Start 2P", cursive;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${themes.spacing(1)};
  margin-top: ${themes.spacing(2)};
  & > button {
    width: 49%;
  }
  @media screen and (max-width: 420px) {
    display: block;
    & > button {
      width: 100%;
      margin-bottom: ${themes.spacing(1)};
    }
  }
`;

const NftWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%;

  & > iframe {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;
